import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Phone, MessageCircle, Mail, MapPin, Globe, Send, Download,
  Share2, QrCode, Linkedin, Twitter, Instagram, Youtube, Music2,
  TrendingUp, Sparkles, Copy, Check, ArrowRight, Languages,
  Target, GraduationCap, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SocialIcon = ({ network, url }) => {
  const map = {
    linkedin: { Icon: Linkedin, bg: '#0a66c2' },
    twitter: { Icon: Twitter, bg: '#000000' },
    instagram: { Icon: Instagram, bg: '#e1306c' },
    youtube: { Icon: Youtube, bg: '#ff0000' },
    tiktok: { Icon: Music2, bg: '#010101' },
  };
  const entry = map[network];
  if (!entry || !url) return null;
  const { Icon, bg } = entry;
  return (
    <a
      href={url} target="_blank" rel="noreferrer noopener"
      data-testid={`card-social-${network}`}
      className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-md transition transform hover:scale-105 active:scale-95"
      style={{ background: bg }}
      aria-label={network}
    >
      <Icon className="w-5 h-5" />
    </a>
  );
};

const ActionTile = ({ icon: Icon, label, color = '#106B3A', onClick, href, testid }) => {
  const inner = (
    <>
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm"
           style={{ background: `${color}1a`, color }}>
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-sm font-medium text-slate-800 mt-2">{label}</span>
    </>
  );
  const wrapClass = "flex flex-col items-center justify-center bg-white rounded-2xl py-4 px-2 border border-slate-100 shadow-sm transition transform hover:scale-[1.03] active:scale-95";
  if (href) {
    return (
      <a href={href} className={wrapClass} data-testid={testid}>{inner}</a>
    );
  }
  return (
    <button onClick={onClick} className={wrapClass} data-testid={testid}>{inner}</button>
  );
};

export default function DigitalCard() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const showOnboarding = searchParams.get('onboard') === '1';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tapCount, setTapCount] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confetti, setConfetti] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [onboardStep, setOnboardStep] = useState(1);
  const [onboardLang, setOnboardLang] = useState(null);
  const [onboardGoal, setOnboardGoal] = useState(null);
  const cardUrl = `${window.location.origin}/c/${slug}`;

  const dismissOnboarding = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('onboard');
    setSearchParams(next, { replace: true });
  };

  const goToSignup = () => {
    const params = new URLSearchParams();
    if (profile?.referral_code) params.set('ref', profile.referral_code);
    if (onboardLang) params.set('lang', onboardLang);
    if (onboardGoal) params.set('goal', onboardGoal);
    window.location.href = `/register${params.toString() ? '?' + params.toString() : ''}`;
  };

  const fireConfetti = useCallback(() => {
    const pieces = Array.from({ length: 28 }, (_, i) => ({
      id: Date.now() + i,
      left: Math.random() * 100,
      delay: Math.random() * 0.3,
      color: ['#106B3A', '#1a3a6b', '#f5b842', '#e1306c', '#ffffff'][i % 5],
      duration: 1.6 + Math.random() * 0.8,
    }));
    setConfetti(pieces);
    setTimeout(() => setConfetti([]), 2800);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/card/${slug}`);
        if (cancelled) return;
        setProfile(data);
        // Fire-and-forget tap counter
        axios.post(`${API}/api/card/${slug}/tap`)
          .then(r => !cancelled && setTapCount(r.data?.tap_count))
          .catch(() => {});
      } catch (e) {
        setProfile({ notFound: true });
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.name} - ${profile.company}`,
          text: profile.tagline,
          url: cardUrl,
        });
      } catch (_) { /* user cancelled */ }
    } else {
      navigator.clipboard?.writeText(cardUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleSaveContact = () => {
    window.location.href = `${API}/api/card/${slug}/vcard`;
    fireConfetti();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white">
        <div className="animate-pulse text-emerald-700 font-medium">Loading…</div>
      </div>
    );
  }
  if (!profile || profile.notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6 text-center">
        <div>
          <div className="text-5xl mb-3">😕</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Card not found</h1>
          <p className="text-slate-600">This digital business card does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const phoneDigits = (profile.phone || '').replace(/[^+\d]/g, '');
  const waDigits = (profile.whatsapp || '').replace(/[^\d]/g, '');
  const photoSrc = profile.photo_url?.startsWith('http')
    ? profile.photo_url
    : `${API}${profile.photo_url}`;
  const videoSrc = profile.video_intro_url?.startsWith('http')
    ? profile.video_intro_url
    : `${API}${profile.video_intro_url}`;

  const signupHref = profile.referral_code
    ? `/register?ref=${encodeURIComponent(profile.referral_code)}`
    : '/register';

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at top, #d8f1e1 0%, #f6fbf7 45%, #ffffff 100%)',
      }}
      data-testid="digital-card-page"
    >
      {/* Confetti burst */}
      {confetti.map(c => (
        <span
          key={c.id}
          className="absolute top-0 w-2 h-3 rounded-sm pointer-events-none"
          style={{
            left: `${c.left}%`,
            background: c.color,
            animation: `cardConfetti ${c.duration}s ${c.delay}s ease-in forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes cardConfetti {
          0%   { transform: translateY(-10vh) rotate(0deg);   opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(120vh) rotate(720deg); opacity: 0.9; }
        }
        @keyframes cardFadeIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .card-stagger > * { opacity: 0; animation: cardFadeIn .55s ease-out forwards; }
        .card-stagger > *:nth-child(1){ animation-delay: .05s; }
        .card-stagger > *:nth-child(2){ animation-delay: .12s; }
        .card-stagger > *:nth-child(3){ animation-delay: .19s; }
        .card-stagger > *:nth-child(4){ animation-delay: .26s; }
        .card-stagger > *:nth-child(5){ animation-delay: .33s; }
        .card-stagger > *:nth-child(6){ animation-delay: .40s; }
        .card-stagger > *:nth-child(7){ animation-delay: .47s; }
      `}</style>

      <div className="max-w-md mx-auto px-5 pt-8 pb-16 card-stagger">

        {/* Header (brand + share) */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                 style={{ background: '#106B3A' }}>
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Wekeza-Smart</span>
          </div>
          <button
            onClick={handleShare}
            data-testid="card-share-btn"
            className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 active:scale-95 transition"
            aria-label="Share card"
          >
            {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Share2 className="w-5 h-5" />}
          </button>
        </div>

        {/* Photo / video */}
        <div className="relative w-full rounded-3xl overflow-hidden shadow-xl bg-emerald-700 aspect-[4/5] mb-5">
          {profile.video_intro_url ? (
            <video
              src={videoSrc}
              poster={photoSrc}
              autoPlay muted loop playsInline
              className="w-full h-full object-cover"
              data-testid="card-video"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : null}
          <img
            src={photoSrc}
            alt={profile.name}
            className="absolute inset-0 w-full h-full object-cover -z-10"
            data-testid="card-photo"
          />
          <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white">
            <div className="flex items-center gap-1 text-xs font-medium opacity-90">
              <Sparkles className="w-3 h-3" /> Digital Business Card
            </div>
            <h1 className="text-2xl font-bold leading-tight mt-1" data-testid="card-name">{profile.name}</h1>
            <p className="text-sm opacity-90" data-testid="card-role">
              {profile.role}{profile.company ? ` · ${profile.company}` : ''}
            </p>
          </div>
        </div>

        {/* Tagline + bio */}
        {profile.tagline && (
          <p className="text-base text-slate-800 font-medium leading-relaxed mb-2"
             data-testid="card-tagline">
            {profile.tagline}
          </p>
        )}
        {profile.bio && (
          <p className="text-sm text-slate-600 leading-relaxed mb-5"
             data-testid="card-bio">
            {profile.bio}
          </p>
        )}

        {/* Primary CTA */}
        <a
          href={signupHref}
          data-testid="card-cta-signup"
          className="block w-full text-center py-4 rounded-full font-semibold text-white shadow-lg mb-5 transition active:scale-[.98]"
          style={{ background: '#106B3A' }}
        >
          Start learning on Wekeza-Smart
        </a>

        {/* Action grid */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          {phoneDigits && (
            <ActionTile icon={Phone} label="Call" testid="card-action-call"
                        href={`tel:${phoneDigits}`} />
          )}
          {waDigits && (
            <ActionTile icon={MessageCircle} label="WhatsApp" color="#25d366" testid="card-action-whatsapp"
                        href={`https://wa.me/${waDigits}`} />
          )}
          {profile.email && (
            <ActionTile icon={Mail} label="Email" color="#1a3a6b" testid="card-action-email"
                        href={`mailto:${profile.email}`} />
          )}
          {phoneDigits && (
            <ActionTile icon={Send} label="SMS" color="#3b82f6" testid="card-action-sms"
                        href={`sms:${phoneDigits}`} />
          )}
          {profile.website && (
            <ActionTile icon={Globe} label="Website" color="#0ea5e9" testid="card-action-website"
                        href={profile.website} />
          )}
          <ActionTile icon={Download} label="Save" color="#f5b842" testid="card-action-save"
                      onClick={handleSaveContact} />
          <ActionTile icon={QrCode} label="My QR" color="#7c3aed" testid="card-action-qr"
                      onClick={() => setQrOpen(true)} />
          <ActionTile icon={Share2} label="Share" color="#e1306c" testid="card-action-share"
                      onClick={handleShare} />
        </div>

        {/* Socials */}
        {profile.socials && Object.keys(profile.socials).length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {Object.entries(profile.socials).map(([k, v]) => (
              <SocialIcon key={k} network={k} url={v} />
            ))}
          </div>
        )}

        {/* Info chips */}
        <div className="space-y-2 mb-7">
          {(profile.address_city || profile.address_country) && (
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <MapPin className="w-4 h-4 text-emerald-700" />
              {[profile.address_city, profile.address_country].filter(Boolean).join(', ')}
            </div>
          )}
        </div>

        {/* Live tap counter (gentle social proof) */}
        {tapCount !== null && tapCount > 0 && (
          <div className="text-center text-xs text-slate-500 mb-4" data-testid="card-tap-count">
            Card opened {tapCount.toLocaleString()} {tapCount === 1 ? 'time' : 'times'}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-[11px] text-slate-400 leading-relaxed">
          Powered by <span className="font-semibold text-emerald-700">Wekeza-Smart</span> ·
          {' '}<a href="/" className="underline">wekeza-smart.co.tz</a>
        </div>
      </div>

      {/* QR modal */}
      {qrOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setQrOpen(false)}
          data-testid="card-qr-modal"
        >
          <div
            className="bg-white rounded-3xl p-6 max-w-xs w-full text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-slate-900 text-lg mb-1">Scan to open this card</h3>
            <p className="text-xs text-slate-500 mb-4">{cardUrl}</p>
            <img
              src={`${API}/api/card/${slug}/qr.png`}
              alt="QR code"
              className="w-full h-auto rounded-xl mb-4"
              data-testid="card-qr-image"
            />
            <button
              onClick={() => {
                navigator.clipboard?.writeText(cardUrl);
                setCopied(true); setTimeout(() => setCopied(false), 1500);
              }}
              className="w-full py-3 rounded-full bg-emerald-700 text-white font-medium flex items-center justify-center gap-2"
              data-testid="card-qr-copy-link-btn"
            >
              {copied ? <><Check className="w-4 h-4" />Copied</> : <><Copy className="w-4 h-4" />Copy link</>}
            </button>
            <a
              href={`${API}/api/card/${slug}/qr.png`}
              download={`${slug}-qr.png`}
              className="block mt-3 text-sm text-emerald-700 underline"
              data-testid="card-qr-download-link"
            >
              Download QR (PNG)
            </a>
          </div>
        </div>
      )}

      {/* Onboarding overlay (triggered by ?onboard=1) */}
      {showOnboarding && profile && !profile.notFound && (
        <div
          className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-md flex items-end sm:items-center justify-center"
          data-testid="card-onboarding-overlay"
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl relative overflow-hidden"
            style={{ animation: 'cardFadeIn .4s ease-out' }}
          >
            {/* Brand stripe */}
            <div className="absolute top-0 left-0 right-0 h-1.5"
                 style={{ background: 'linear-gradient(90deg,#106B3A,#1a8a4a,#106B3A)' }} />
            {/* Dismiss */}
            <button
              onClick={dismissOnboarding}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"
              aria-label="Close onboarding"
              data-testid="card-onboarding-close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mb-5 mt-1">
              {[1,2,3].map(n => (
                <div key={n}
                  className="h-1 rounded-full transition-all"
                  style={{
                    width: n === onboardStep ? 28 : 10,
                    background: n <= onboardStep ? '#106B3A' : '#e2e8f0',
                  }}
                />
              ))}
            </div>

            {/* Step 1 — Language */}
            {onboardStep === 1 && (
              <div data-testid="card-onboard-step-1">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                     style={{ background: '#106B3A1a', color: '#106B3A' }}>
                  <Languages className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">Karibu! Welcome 👋</h2>
                <p className="text-sm text-slate-600 mb-5">
                  Choose your language to start learning.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { code: 'sw', label: 'Kiswahili', sub: 'Lugha ya kuanzia' },
                    { code: 'en', label: 'English', sub: 'Start here' },
                  ].map(opt => (
                    <button
                      key={opt.code}
                      onClick={() => { setOnboardLang(opt.code); setOnboardStep(2); }}
                      className={`text-left p-4 rounded-2xl border-2 transition active:scale-95 ${
                        onboardLang === opt.code ? 'border-emerald-700 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`card-onboard-lang-${opt.code}`}
                    >
                      <div className="font-semibold text-slate-900">{opt.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{opt.sub}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 text-center">
                  Brought to you by {profile.name} · {profile.company}
                </p>
              </div>
            )}

            {/* Step 2 — Goal */}
            {onboardStep === 2 && (
              <div data-testid="card-onboard-step-2">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                     style={{ background: '#1a3a6b1a', color: '#1a3a6b' }}>
                  <Target className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">
                  {onboardLang === 'sw' ? 'Lengo lako ni nini?' : 'What\u2019s your goal?'}
                </h2>
                <p className="text-sm text-slate-600 mb-5">
                  {onboardLang === 'sw'
                    ? 'Tutakuonyesha hatua sahihi za kuanzia.'
                    : 'We\u2019ll show you the right steps to start.'}
                </p>
                <div className="space-y-2.5 mb-4">
                  {(onboardLang === 'sw'
                    ? [
                        { id: 'learn',    icon: '📚', label: 'Kujifunza misingi ya uwekezaji' },
                        { id: 'compare',  icon: '⚖️', label: 'Kulinganisha hisa, UTT na hatifungani' },
                        { id: 'simulate', icon: '🎯', label: 'Kufanya majaribio kabla ya kuwekeza' },
                        { id: 'coach',    icon: '🤖', label: 'Kuzungumza na mkocha wa AI' },
                      ]
                    : [
                        { id: 'learn',    icon: '📚', label: 'Learn the basics of investing' },
                        { id: 'compare',  icon: '⚖️', label: 'Compare stocks, UTT funds, bonds' },
                        { id: 'simulate', icon: '🎯', label: 'Practice with a simulated portfolio' },
                        { id: 'coach',    icon: '🤖', label: 'Chat with the AI coach' },
                      ]
                  ).map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => { setOnboardGoal(opt.id); setOnboardStep(3); }}
                      className={`w-full text-left px-4 py-3 rounded-2xl border-2 transition active:scale-[0.98] flex items-center gap-3 ${
                        onboardGoal === opt.id ? 'border-emerald-700 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`card-onboard-goal-${opt.id}`}
                    >
                      <span className="text-xl">{opt.icon}</span>
                      <span className="text-sm font-medium text-slate-900">{opt.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setOnboardStep(1)}
                  className="text-xs text-slate-500 underline"
                >
                  {onboardLang === 'sw' ? 'Rudi nyuma' : 'Back'}
                </button>
              </div>
            )}

            {/* Step 3 — Sign-up CTA */}
            {onboardStep === 3 && (
              <div data-testid="card-onboard-step-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                     style={{ background: '#f5b8421a', color: '#b07208' }}>
                  <GraduationCap className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">
                  {onboardLang === 'sw'
                    ? 'Tuko tayari kuanza!'
                    : 'You\u2019re ready to start!'}
                </h2>
                <p className="text-sm text-slate-600 mb-5">
                  {onboardLang === 'sw'
                    ? 'Tengeneza akaunti yako ya bure - inachukua chini ya dakika moja.'
                    : 'Create your free account - takes less than a minute.'}
                </p>
                <div className="bg-emerald-50 rounded-2xl p-4 mb-5 border border-emerald-100">
                  <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-2">
                    {onboardLang === 'sw' ? 'Utapata' : 'You\u2019ll get'}
                  </div>
                  <ul className="text-sm text-slate-700 space-y-1.5">
                    <li>✓ {onboardLang === 'sw' ? 'Masomo ya bure (Swahili + English)' : 'Free lessons (Swahili + English)'}</li>
                    <li>✓ {onboardLang === 'sw' ? 'Mkocha wa AI wa moja kwa moja' : 'Instant AI investment coach'}</li>
                    <li>✓ {onboardLang === 'sw' ? 'Mfumo wa kufanya simulation' : 'Simulated portfolio sandbox'}</li>
                    <li>✓ {onboardLang === 'sw' ? 'Linganisha hisa, UTT na hatifungani' : 'Compare stocks, UTT, bonds'}</li>
                  </ul>
                </div>
                <button
                  onClick={goToSignup}
                  className="w-full py-4 rounded-full font-semibold text-white shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{ background: '#106B3A' }}
                  data-testid="card-onboard-signup-btn"
                >
                  {onboardLang === 'sw' ? 'Jisajili sasa' : 'Create my free account'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setOnboardStep(2); }}
                  className="w-full mt-2 text-xs text-slate-500 underline"
                >
                  {onboardLang === 'sw' ? 'Rudi nyuma' : 'Back'}
                </button>
                <button
                  onClick={dismissOnboarding}
                  className="w-full mt-3 text-xs text-slate-400"
                  data-testid="card-onboard-skip"
                >
                  {onboardLang === 'sw' ? 'Ruka, niangalie kadi tu' : 'Skip - just show me the card'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
